/**
 * consultationSlots.js
 *
 * Single source of truth for consultation time slots and the rules deciding
 * which of them a customer may still book. Imported by both landing page
 * variants so the two can't drift apart.
 *
 * Everything is anchored to IST. India has no DST, so IST is a constant +05:30 —
 * the same fixed-offset convention the backend already uses in CONVERT_TZ. That
 * lets us do plain epoch arithmetic instead of pulling in a timezone database.
 */

/** IST is UTC+05:30, year round. */
export const IST_OFFSET_MIN = 330;

/** A slot must start at least this far ahead to be offered. */
export const SLOT_LEAD_TIME_MIN = 120;

/** How many days past today a customer may book. */
export const MAX_BOOKING_DAYS_AHEAD = 15;

const MS_PER_MIN = 60 * 1000;
const MS_PER_DAY = 24 * 60 * MS_PER_MIN;

/**
 * `value` is written to leads.consultation_slot and matched by the astrologer
 * app — these strings must not change. `startMin` is minutes from IST midnight.
 */
export const CONSULTATION_SLOTS = [
  { value: '10:00 AM - 10:30 AM', startMin: 600 },
  { value: '10:30 AM - 11:00 AM', startMin: 630 },
  { value: '11:00 AM - 11:30 AM', startMin: 660 },
  { value: '11:30 AM - 12:00 PM', startMin: 690 },
  { value: '12:00 PM - 12:30 PM', startMin: 720 },
  { value: '12:30 PM - 01:00 PM', startMin: 750 },
  { value: '01:00 PM - 01:30 PM', startMin: 780 },
  { value: '01:30 PM - 02:00 PM', startMin: 810 },
  { value: '02:00 PM - 02:30 PM', startMin: 840 },
  { value: '02:30 PM - 03:00 PM', startMin: 870 },
  { value: '03:00 PM - 03:30 PM', startMin: 900 },
  { value: '03:30 PM - 04:00 PM', startMin: 930 },
  { value: '04:00 PM - 04:30 PM', startMin: 960 },
  { value: '04:30 PM - 05:00 PM', startMin: 990 },
];

/**
 * Display label. Slots are always IST — say so, so an overseas customer doesn't
 * read them as local time. The stored value stays unlabelled.
 */
export function slotLabel(slot) {
  return `${slot.value} IST`;
}

/**
 * IST calendar fields for an epoch timestamp. Shifting the epoch by the IST
 * offset and then reading UTC fields yields IST date/time with no tz library.
 */
function istParts(nowMs = Date.now()) {
  const shifted = new Date(nowMs + IST_OFFSET_MIN * MS_PER_MIN);
  return {
    date: shifted.toISOString().slice(0, 10), // 'YYYY-MM-DD' in IST
    minutesOfDay: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
  };
}

/** Today's date in IST, as 'YYYY-MM-DD'. */
export function istDate(nowMs = Date.now()) {
  return istParts(nowMs).date;
}

/** IST date `days` after the given moment, as 'YYYY-MM-DD'. */
export function istDatePlusDays(nowMs = Date.now(), days = 0) {
  return istDate(nowMs + days * MS_PER_DAY);
}

/**
 * Slots still bookable on `selectedDate` ('YYYY-MM-DD', read as IST).
 * Future dates get the full list, today is filtered by the lead time, past
 * dates get nothing. Dates in this format compare correctly with < and >.
 */
export function availableSlots(selectedDate, nowMs = Date.now()) {
  if (!selectedDate) return [];
  const { date: today, minutesOfDay } = istParts(nowMs);
  if (selectedDate > today) return CONSULTATION_SLOTS;
  if (selectedDate < today) return [];
  const cutoffMin = minutesOfDay + SLOT_LEAD_TIME_MIN;
  return CONSULTATION_SLOTS.filter((slot) => slot.startMin >= cutoffMin);
}

/**
 * True once every slot today has fallen inside the lead-time window. With slots
 * ending at 5:00 PM this happens at 2:30 PM IST, so it is the normal evening
 * state rather than an edge case.
 */
export function isTodayClosed(nowMs = Date.now()) {
  return availableSlots(istDate(nowMs), nowMs).length === 0;
}

/** Earliest date with at least one bookable slot — today, else tomorrow. */
export function firstBookableDate(nowMs = Date.now()) {
  return isTodayClosed(nowMs) ? istDatePlusDays(nowMs, 1) : istDate(nowMs);
}

/** Latest bookable date, counted from today in IST. */
export function lastBookableDate(nowMs = Date.now()) {
  return istDatePlusDays(nowMs, MAX_BOOKING_DAYS_AHEAD);
}
